import { Observable, catchError, from, map, of, switchMap, tap } from 'rxjs';
import { ExportByTypeService } from './export-resolver.model';
import { ExportExecutableParams } from '../export.model';
import { Injectable, inject } from '@angular/core';
import { HttpStreamsService } from '@cbre/http';
import { Workbook } from 'exceljs';
import { BG_COLORS, COLUMNS, DATA_TYPE_FORMATTERS, PVExcelEntry } from './pv-export-excel.config';
import saveAs from 'file-saver';
import { Worksheet } from 'exceljs';
import { MiqMoment } from '@miq/utils';
import { HttpParams } from '@angular/common/http';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@Injectable({ providedIn: 'root' })
export class PVExportExcelService implements ExportByTypeService<ExportExecutableParams, boolean> {
  private readonly columns = COLUMNS;
  private readonly logger: CbreLogger = inject(CbreLoggerToken);

  constructor(private httpClient: HttpStreamsService) {}

  execute(options: ExportExecutableParams): Observable<boolean> {
    const { viewState = {}, dialogOptions } = options;

    const usageTypes = viewState?.propertyType ?? null;
    const params: any = usageTypes != null ? { usageTypes } : {};
    if (dialogOptions?.user_conf) {
      params.user_conf = dialogOptions.user_conf;
    }
    const httpParams = new HttpParams({ fromObject: params });

    return this.httpClient.httpGet(`/stack2/${options.propertyId}/pv/table`, httpParams).pipe(
      map(res => res.results),
      switchMap(data => this._generateWorkbook(data, options))
    );
  }

  private _generateWorkbook(data: Record<string, any>[], { propertyData, propertyId }: ExportExecutableParams) {
    const { propertyName, postalAddress } = propertyData;

    const downloadDate = MiqMoment.format(new Date(), 'YYYY-MM-DD');
    const sheetName = `Stack Data - ${
      this._getFileName(propertyName, postalAddress?.full_street_name) ?? propertyId
    } - ${downloadDate}`;

    const workbook: Workbook = new Workbook();
    const sheet = workbook.addWorksheet(sheetName, {
      properties: { defaultColWidth: 22 },
      pageSetup: { fitToPage: true, fitToHeight: 5, fitToWidth: 7 },
    });
    // set column config
    sheet.columns = this.columns;

    // add rows
    for (const row of data) {
      sheet.addRow(this._formatData(row, this.columns));
    }

    // style and configure sheet
    this._styleSheet(sheet);
    this._setAutoFilter(sheet);

    // write file
    return from(workbook.xlsx.writeBuffer()).pipe(
      tap((data: any) => saveAs(new Blob([data]), `${sheetName}.xlsx`)),
      map(() => true),
      catchError(err => {
        this.logger.error(`Error generating PV workbook: ${err?.message ?? 'unknown error'}`);
        return of(false);
      })
    );
  }

  private _getFileName(propertyName: string, postalAddress: string) {
    if (propertyName == null || postalAddress == null) {
      return propertyName ?? postalAddress;
    }

    const propertyNameLower = propertyName.toLowerCase();
    const postalAddressLower = postalAddress.toLowerCase();
    if (propertyNameLower.includes(postalAddressLower)) {
      return propertyName;
    }
    if (postalAddressLower.includes(propertyNameLower)) {
      return postalAddress;
    }

    return `${propertyName} ${postalAddress}`;
  }

  /**
   * uses row count and column count to set up the exeljs auto filter
   * @param sheet
   */
  private _setAutoFilter(sheet: Worksheet): void {
    sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: sheet.rowCount, column: sheet.columnCount } };
  }

  private _styleSheet(worksheet: Worksheet) {
    // Set the fill color of the header row
    worksheet.getRow(1).eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BG_COLORS.HEADER } };
      cell.font = { name: 'Calibri', color: { argb: 'FFFFFFFF' }, bold: true };
    });

    // Set table pattern
    for (let i = 2; i <= worksheet.rowCount; i++) {
      const row = worksheet.getRow(i);
      const isOddRow = i % 2 === 0;
      row.eachCell(cell => {
        cell.alignment = { horizontal: 'left' };
        cell.font = { name: 'Calibri' };
        if (isOddRow) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BG_COLORS.ODD } };
        }
      });
    }
  }

  private _formatData = (row: Record<string, any>, columns: PVExcelEntry[]): Record<string, any> => {
    const formatted = columns.map(col => {
      let value = row[col.key];

      // Lease Type logic
      if (col.key === 'type' && row.lease_record_type_desc === 'Owner Occupied') {
        const status = row.type?.trim();
        value =
          !status || status.toLowerCase() === 'occupying' || ['direct', 'sublease'].includes(status.toLowerCase())
            ? 'Owner'
            : `Owner (${status})`;
      }
      // End Date logic
      if (col.key === 'tenancy_end_date' && row.lease_record_type_desc === 'Owner Occupied') {
        if (!row.tenancy_end_date) {
          return { [col.key]: 'N/A' };
        }
        value = row.tenancy_end_date;
      }
      return { [col.key]: this._formatByDataType(value, col) };
    });
    return Object.assign({}, row, ...formatted);
  };
  private _formatByDataType = (value: any, { dataType, lookupCategory, values }: PVExcelEntry): any => {
    if (lookupCategory != null) {
      return values.find(option => option.key === value)?.value ?? value;
    }

    const formatter = DATA_TYPE_FORMATTERS[dataType];
    return formatter ? formatter(value) : value;
  };
}
